import React, { useEffect, useState, useRef } from 'react';
import {
  DollarSign, TrendingUp, Users, FileText, Shield, Percent, Wallet,
  RefreshCw, Loader2, AlertTriangle, CheckCircle, XCircle, CreditCard,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Eye, Fingerprint,
  Clock, Target, Ban, FileBarChart, Printer
} from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import DraggableTabBar from '../components/DraggableTabBar';
import '../styles/StripeReportsPage.css';
import PanelLoadError from '../components/PanelLoadError';
import { logLoadError } from '../lib/loadError';

function fmt(c) { return (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }); }
function fmtD(ts) { if (!ts) return '—'; return new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleDateString(); }
function pct(n) { return typeof n === 'string' ? n + '%' : n.toFixed(1) + '%'; }

function StripeReportsPage({ user }) {
  const [tab, setTab] = useState('financial');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState({});
  const setL = (k, v) => setLoading(p => ({ ...p, [k]: v }));
  const tabsRef = useRef(null);
  const { onKeyDown: handleTabKeyDown } = useArrowNav(tabsRef, 'button', { vertical: false, activateOnFocus: true });

  const [financial, setFinancial] = useState(null);
  const [payments, setPayments] = useState(null);
  const [profiles, setProfiles] = useState(null);
  const [tax, setTax] = useState(null);
  const [fraud, setFraud] = useState(null);
  const [billing, setBilling] = useState(null);
  const [payoutData, setPayoutData] = useState(null);

  /*
   * ⛔⛔ EVERY ONE OF THESE SEVEN LOADERS USED TO END `catch {}` — Card P114
   * PR-3, and the empty braces were the whole defect.
   *
   * A 401 from a wrong Stripe key, a 500, a dropped connection and a
   * genuinely quiet month all left the panel's state at `null`, which the
   * render below printed as "No payment data available". The console said
   * nothing either. Two staff members reported these panels as dead clicks on
   * 23-24 Oct; from the screen, dead and empty were the same thing.
   *
   * `run()` is the one place that decides, so the seven cannot drift:
   *   · resolves with success  -> set the data, CLEAR any previous error
   *   · resolves {success:false} -> that is a FAILURE, not an empty result
   *   · throws                 -> failure
   *
   * ⚠ THE SECOND BRANCH IS THE SUBTLE ONE. `if (r?.success)` already existed;
   * what was missing was an else. A handler that answers "I could not reach
   * Stripe" politely, with success:false, was indistinguishable from one that
   * answered "nothing happened this month".
   *
   * ⚠ NOTHING PROVIDER-SUPPLIED IS KEPT. We store the allowlisted shape, not
   * the error — see src/lib/loadError.js. `r.error` is deliberately NOT read
   * into the message; only a status/code can survive.
   */
  const [errors, setErrors] = useState({});
  const setErr = (k, v) => setErrors(p => ({ ...p, [k]: v }));

  async function run(key, what, call, apply) {
    setL(key, true);
    try {
      const r = await call();
      if (r?.success) { apply(r); setErr(key, null); }
      else { logLoadError(what, r); setErr(key, r || { code: 'refused' }); }
    } catch (err) {
      logLoadError(what, err);
      setErr(key, err || { code: 'unknown' });
    } finally { setL(key, false); }
  }

  const loadFinancial = () => run('fin',   'the financial overview', () => window.zignagenixAPI.stripeReportFinancial(),      r => setFinancial(r.overview));
  const loadPayments  = () => run('pay',   'the payment report',     () => window.zignagenixAPI.stripeReportPayments(days),   r => setPayments(r.breakdown));
  const loadProfiles  = () => run('prof',  'customer profiles',      () => window.zignagenixAPI.stripeReportProfiles(days),   r => setProfiles(r.profiles));
  const loadTax       = () => run('tax',   'the tax report',         () => window.zignagenixAPI.stripeReportTax(days),        r => setTax(r.taxSummary));
  const loadFraud     = () => run('fraud', 'the trust & safety report', () => window.zignagenixAPI.stripeReportFraud(),       r => setFraud(r.fraudReport));
  const loadBilling   = () => run('bill',  'the billing report',     () => window.zignagenixAPI.stripeReportBilling(days),    r => setBilling(r.billingSummary));
  const loadPayouts   = () => run('pout',  'the payout report',      () => window.zignagenixAPI.stripeReportPayouts(days),    r => setPayoutData(r.payoutSummary));

  useEffect(() => {
    switch (tab) {
      case 'financial': loadFinancial(); break;
      case 'payments': loadPayments(); break;
      case 'profiles': loadProfiles(); break;
      case 'tax': loadTax(); break;
      case 'fraud': loadFraud(); break;
      case 'billing': loadBilling(); break;
      case 'payouts': loadPayouts(); break;
    }
  }, [tab, days]);

  const tabs = [
    { id: 'financial', label: 'Financial Overview', icon: DollarSign },
    { id: 'payments', label: 'Payments & Billing', icon: CreditCard },
    { id: 'billing', label: 'Invoices & Subs', icon: FileText },
    { id: 'profiles', label: 'Customer Profiles', icon: Users },
    { id: 'tax', label: 'Tax Report', icon: Percent },
    { id: 'fraud', label: 'Trust & Safety', icon: Shield },
    { id: 'payouts', label: 'Payouts', icon: Wallet },
  ];

  const Spin = () => <Loader2 size={24} className="rpt-spin" />;

  // ── Metric Card helper ────────────────────────────────────────────
  const Metric = ({ icon: Icon, label, value, sub, color }) => (
    <div className={`rpt-metric ${color ? 'rpt-metric--' + color : ''}`}>
      <Icon size={20} />
      <div className="rpt-metric-value">{value}</div>
      <div className="rpt-metric-label">{label}</div>
      {sub && <div className="rpt-metric-sub">{sub}</div>}
    </div>
  );

  // ── Bar helper (horizontal bar chart) ──────────────────────────────
  const HBar = ({ items, maxVal }) => (
    <div className="rpt-hbar-list">
      {items.map((item, i) => (
        <div key={i} className="rpt-hbar-row">
          <span className="rpt-hbar-label">{item.label}</span>
          <div className="rpt-hbar-track">
            <div className="rpt-hbar-fill" style={{ width: maxVal > 0 ? `${(item.value / maxVal) * 100}%` : '0%' }} />
          </div>
          <span className="rpt-hbar-val">{item.display}</span>
        </div>
      ))}
    </div>
  );

  const currentTabLabel = tabs.find(t => t.id === tab)?.label || '';

  return (
    <div className="page rpt-page">
      <div className="rpt-print-header">
        <h1>ZignaGenix Medical Intelligence Platform</h1>
        <h2>Stripe {currentTabLabel} Report</h2>
        <p>Period: Last {days} days &nbsp;|&nbsp; Generated: {new Date().toLocaleString()}</p>
      </div>
      <div className="rpt-header">
        <div>
          <h1 className="page-title">ZignaGenix Stripe Reports</h1>
          <p className="page-subtitle">Consolidated financial reporting pulled from your Stripe account</p>
        </div>
        <div className="rpt-header-controls">
          <div className="rpt-period-select">
            <label>Period:</label>
            <select value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <button className="rpt-print-btn" onClick={() => window.print()} title="Print Report">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>

      <DraggableTabBar
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
        prefKey="stripe_reports_tab_order"
        className="rpt-tabs"
        tabClassName="rpt-tab"
        activeClassName="rpt-tab--active"
      />

      <div className="rpt-content">

        {/* ═══ FINANCIAL OVERVIEW ═══ */}
        {tab === 'financial' && (
          loading.fin ? <div className="rpt-loading"><Spin /> Generating financial overview...</div> : errors.fin ? <PanelLoadError what="the financial overview" error={errors.fin} onRetry={loadFinancial} /> : financial ? (
            <div className="rpt-section">
              <div className="rpt-metric-grid rpt-metric-grid--4">
                <Metric icon={DollarSign} label="Total Revenue" value={fmt(financial.totalRevenue)} sub={`${financial.successCount} successful charges`} color="green" />
                <Metric icon={ArrowDownRight} label="Total Refunded" value={fmt(financial.totalRefunded)} sub={`${financial.refundCount} refunds`} color="red" />
                <Metric icon={AlertTriangle} label="Disputes" value={fmt(financial.totalDisputeAmount)} sub={`${financial.openDisputeCount} open of ${financial.totalDisputeCount}`} color="yellow" />
                <Metric icon={Wallet} label="Total Paid Out" value={fmt(financial.totalPaidOut)} sub={`${fmt(financial.pendingPayouts)} pending`} color="blue" />
              </div>

              <div className="rpt-row-2">
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><TrendingUp size={18} /> Revenue Summary</h3>
                  <div className="rpt-kv-list">
                    <div className="rpt-kv"><span>Gross Revenue</span><strong className="text-green">{fmt(financial.totalRevenue)}</strong></div>
                    <div className="rpt-kv"><span>Refunds</span><strong className="text-red">-{fmt(financial.totalRefunded)}</strong></div>
                    <div className="rpt-kv"><span>Dispute Losses</span><strong className="text-red">-{fmt(financial.totalDisputeAmount)}</strong></div>
                    <div className="rpt-kv rpt-kv--total"><span>Net Revenue</span><strong>{fmt(financial.totalRevenue - financial.totalRefunded - financial.totalDisputeAmount)}</strong></div>
                  </div>
                </div>
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><BarChart3 size={18} /> Charge Performance</h3>
                  <div className="rpt-kv-list">
                    <div className="rpt-kv"><span>Total Charges</span><strong>{financial.chargeCount}</strong></div>
                    <div className="rpt-kv"><span>Successful</span><strong className="text-green">{financial.successCount}</strong></div>
                    <div className="rpt-kv"><span>Failed</span><strong className="text-red">{financial.failedCount}</strong></div>
                    <div className="rpt-kv"><span>Success Rate</span><strong>{financial.chargeCount > 0 ? ((financial.successCount / financial.chargeCount) * 100).toFixed(1) + '%' : 'N/A'}</strong></div>
                  </div>
                </div>
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><Wallet size={18} /> Balance</h3>
                <div className="rpt-balance-grid">
                  {Object.entries(financial.available || {}).map(([cur, amt]) => (
                    <div key={cur} className="rpt-balance-item">
                      <span className="rpt-balance-cur">{cur.toUpperCase()}</span>
                      <span className="rpt-balance-avail">Available: <strong>{fmt(amt)}</strong></span>
                      <span className="rpt-balance-pend">Pending: <strong>{fmt((financial.pending || {})[cur] || 0)}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : <div className="rpt-empty">No financial activity yet</div>
        )}

        {/* ═══ PAYMENTS & BILLING ═══ */}
        {tab === 'payments' && (
          loading.pay ? <div className="rpt-loading"><Spin /> Analyzing payments...</div> : errors.pay ? <PanelLoadError what="the payment report" error={errors.pay} onRetry={loadPayments} /> : payments ? (
            <div className="rpt-section">
              <div className="rpt-row-2">
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><PieChart size={18} /> By Status</h3>
                  {Object.entries(payments.byStatus).map(([status, d]) => (
                    <div key={status} className="rpt-kv">
                      <span><span className={`rpt-dot rpt-dot--${status}`} /> {status}</span>
                      <strong>{d.count} charges — {fmt(d.amount)}</strong>
                    </div>
                  ))}
                </div>
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><CreditCard size={18} /> By Payment Method</h3>
                  <HBar
                    items={Object.entries(payments.byMethod).map(([m, d]) => ({ label: m, value: d.amount, display: `${d.count} — ${fmt(d.amount)}` }))}
                    maxVal={Math.max(...Object.values(payments.byMethod).map(d => d.amount), 1)}
                  />
                </div>
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><TrendingUp size={18} /> Daily Revenue (Last {days} Days)</h3>
                {Object.keys(payments.dailyRevenue).length > 0 ? (
                  <HBar
                    items={Object.entries(payments.dailyRevenue).sort().map(([day, amt]) => ({ label: day, value: amt, display: fmt(amt) }))}
                    maxVal={Math.max(...Object.values(payments.dailyRevenue), 1)}
                  />
                ) : <p className="rpt-empty-text">No revenue data for this period</p>}
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><Users size={18} /> Top Customers by Spend</h3>
                {payments.topCustomers.length > 0 ? (
                  <HBar
                    items={payments.topCustomers.map(c => ({ label: c.name, value: c.amount, display: fmt(c.amount) }))}
                    maxVal={payments.topCustomers[0]?.amount || 1}
                  />
                ) : <p className="rpt-empty-text">No customer data for this period</p>}
              </div>
            </div>
          ) : <div className="rpt-empty">No payments yet</div>
        )}

        {/* ═══ INVOICES & SUBSCRIPTIONS ═══ */}
        {tab === 'billing' && (
          loading.bill ? <div className="rpt-loading"><Spin /> Loading billing report...</div> : errors.bill ? <PanelLoadError what="the billing report" error={errors.bill} onRetry={loadBilling} /> : billing ? (
            <div className="rpt-section">
              <div className="rpt-metric-grid rpt-metric-grid--4">
                <Metric icon={FileText} label="Total Billed" value={fmt(billing.invoices.totalBilled)} sub={`${billing.invoices.total} invoices`} color="blue" />
                <Metric icon={CheckCircle} label="Collected" value={fmt(billing.invoices.totalCollected)} sub={`${billing.invoices.collectionRate}% collection rate`} color="green" />
                <Metric icon={Clock} label="Outstanding" value={fmt(billing.invoices.totalOutstanding)} sub={`${billing.invoices.byStatus?.open || 0} open invoices`} color="yellow" />
                <Metric icon={RefreshCw} label="MRR" value={fmt(billing.subscriptions.mrr)} sub={`${billing.subscriptions.byStatus?.active || 0} active subs`} color="purple" />
              </div>

              <div className="rpt-row-2">
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><FileText size={18} /> Invoice Breakdown</h3>
                  {Object.entries(billing.invoices.byStatus || {}).map(([st, ct]) => (
                    <div key={st} className="rpt-kv"><span><span className={`rpt-dot rpt-dot--${st}`} /> {st}</span><strong>{ct}</strong></div>
                  ))}
                </div>
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><RefreshCw size={18} /> Subscription Breakdown</h3>
                  {Object.entries(billing.subscriptions.byStatus || {}).map(([st, ct]) => (
                    <div key={st} className="rpt-kv"><span><span className={`rpt-dot rpt-dot--${st}`} /> {st}</span><strong>{ct}</strong></div>
                  ))}
                </div>
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><FileText size={18} /> Recent Invoices</h3>
                <div className="rpt-table-wrap"><table className="rpt-table">
                  <thead><tr><th>Number</th><th>Customer</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>{(billing.recentInvoices || []).map(inv => (
                    <tr key={inv.id}>
                      <td className="rpt-td-mono">{inv.number || '—'}</td><td>{inv.customer_name || '—'}</td>
                      <td className="rpt-td-money">{fmt(inv.total || 0)}</td><td className="rpt-td-money">{fmt(inv.amount_paid || 0)}</td>
                      <td>{fmt(inv.amount_remaining || 0)}</td>
                      <td><span className={`rpt-status rpt-status--${inv.status}`}>{inv.status}</span></td>
                      <td>{fmtD(inv.created)}</td>
                    </tr>
                  ))}{(billing.recentInvoices || []).length === 0 && <tr><td colSpan="7" className="rpt-empty-text">No invoices</td></tr>}</tbody>
                </table></div>
              </div>
            </div>
          ) : <div className="rpt-empty">No invoices or subscriptions yet</div>
        )}

        {/* ═══ CUSTOMER PROFILES ═══ */}
        {tab === 'profiles' && (
          loading.prof ? <div className="rpt-loading"><Spin /> Analyzing customer profiles...</div> : errors.prof ? <PanelLoadError what="customer profiles" error={errors.prof} onRetry={loadProfiles} /> : profiles ? (
            <div className="rpt-section">
              <div className="rpt-metric-grid rpt-metric-grid--4">
                <Metric icon={Users} label="Total Customers" value={profiles.totalCustomers} color="blue" />
                <Metric icon={ArrowUpRight} label="New Customers" value={profiles.newCustomers} sub={`Last ${days} days`} color="green" />
                <Metric icon={RefreshCw} label="Active Subs" value={profiles.activeSubscriptions} color="purple" />
                <Metric icon={XCircle} label="Canceled Subs" value={profiles.canceledSubscriptions} color="red" />
              </div>

              <div className="rpt-row-2">
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><Eye size={18} /> Data Completeness</h3>
                  <div className="rpt-kv-list">
                    <div className="rpt-kv"><span>Has Email</span><strong>{profiles.withEmail} / {profiles.totalCustomers}</strong></div>
                    <div className="rpt-kv"><span>Has Phone</span><strong>{profiles.withPhone} / {profiles.totalCustomers}</strong></div>
                    <div className="rpt-kv"><span>Email %</span><strong>{profiles.totalCustomers > 0 ? ((profiles.withEmail / profiles.totalCustomers) * 100).toFixed(0) + '%' : 'N/A'}</strong></div>
                    <div className="rpt-kv"><span>Phone %</span><strong>{profiles.totalCustomers > 0 ? ((profiles.withPhone / profiles.totalCustomers) * 100).toFixed(0) + '%' : 'N/A'}</strong></div>
                  </div>
                </div>
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><BarChart3 size={18} /> Subscription Health</h3>
                  <div className="rpt-kv-list">
                    <div className="rpt-kv"><span>Active</span><strong className="text-green">{profiles.activeSubscriptions}</strong></div>
                    <div className="rpt-kv"><span>Canceled</span><strong className="text-red">{profiles.canceledSubscriptions}</strong></div>
                    <div className="rpt-kv"><span>Churn Rate</span><strong>{(profiles.activeSubscriptions + profiles.canceledSubscriptions) > 0 ? ((profiles.canceledSubscriptions / (profiles.activeSubscriptions + profiles.canceledSubscriptions)) * 100).toFixed(1) + '%' : 'N/A'}</strong></div>
                  </div>
                </div>
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><Users size={18} /> Customer Directory</h3>
                <div className="rpt-table-wrap"><table className="rpt-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Since</th></tr></thead>
                  <tbody>{(profiles.customers || []).map(c => (
                    <tr key={c.id}><td className="rpt-td-name">{c.name || '—'}</td><td>{c.email || '—'}</td><td>{c.phone || '—'}</td><td>{fmtD(c.created)}</td></tr>
                  ))}</tbody>
                </table></div>
              </div>
            </div>
          ) : <div className="rpt-empty">No customers yet</div>
        )}

        {/* ═══ TAX REPORT ═══ */}
        {tab === 'tax' && (
          loading.tax ? <div className="rpt-loading"><Spin /> Calculating tax report...</div> : errors.tax ? <PanelLoadError what="the tax report" error={errors.tax} onRetry={loadTax} /> : tax ? (
            <div className="rpt-section">
              <div className="rpt-metric-grid">
                <Metric icon={Percent} label="Tax Collected" value={fmt(tax.totalTaxCollected)} sub={`From ${tax.taxableInvoiceCount} taxable invoices`} color="green" />
                <Metric icon={FileText} label="Total Invoices" value={tax.totalInvoiceCount} sub={`Last ${days} days`} color="blue" />
                <Metric icon={Target} label="Tax Rates" value={tax.taxRates.length} sub="Active rates" color="purple" />
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><Percent size={18} /> Active Tax Rates</h3>
                {tax.taxRates.length > 0 ? (
                  <div className="rpt-table-wrap"><table className="rpt-table">
                    <thead><tr><th>Name</th><th>Rate</th><th>Inclusive</th><th>Jurisdiction</th><th>Type</th></tr></thead>
                    <tbody>{tax.taxRates.map(r => (
                      <tr key={r.id}><td className="rpt-td-name">{r.displayName}</td><td><strong>{r.percentage}%</strong></td>
                      <td>{r.inclusive ? 'Yes' : 'No'}</td><td>{r.jurisdiction || '—'}</td><td>{r.taxType || '—'}</td></tr>
                    ))}</tbody>
                  </table></div>
                ) : <p className="rpt-empty-text">No tax rates configured. Set up tax rates in Stripe Dashboard.</p>}
              </div>
            </div>
          ) : <div className="rpt-empty">No tax activity yet</div>
        )}

        {/* ═══ TRUST & SAFETY ═══ */}
        {tab === 'fraud' && (
          loading.fraud ? <div className="rpt-loading"><Spin /> Analyzing trust &amp; safety...</div> : errors.fraud ? <PanelLoadError what="the trust and safety report" error={errors.fraud} onRetry={loadFraud} /> : fraud ? (
            <div className="rpt-section">
              <div className="rpt-metric-grid rpt-metric-grid--4">
                <Metric icon={AlertTriangle} label="Total Disputes" value={fraud.disputes.total} sub={fmt(fraud.disputes.totalAmount)} color="red" />
                <Metric icon={CheckCircle} label="Win Rate" value={fraud.disputes.winRate + '%'} sub={`${fraud.disputes.wonCount}W / ${fraud.disputes.lostCount}L`} color="green" />
                <Metric icon={Shield} label="Fraud Warnings" value={fraud.warnings.total} sub="Early detections" color="yellow" />
                <Metric icon={Ban} label="Dispute Rate" value={fraud.riskMetrics.disputeRate + '%'} sub={`${fraud.riskMetrics.riskyCharges} risky, ${fraud.riskMetrics.blockedCharges} blocked`} color="blue" />
              </div>

              <div className="rpt-row-2">
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><AlertTriangle size={18} /> Disputes by Status</h3>
                  {Object.entries(fraud.disputes.byStatus || {}).map(([st, ct]) => (
                    <div key={st} className="rpt-kv"><span><span className={`rpt-dot rpt-dot--${st}`} /> {st}</span><strong>{ct}</strong></div>
                  ))}
                  {Object.keys(fraud.disputes.byStatus || {}).length === 0 && <p className="rpt-empty-text">No disputes — excellent!</p>}
                </div>
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><Shield size={18} /> Disputes by Reason</h3>
                  {Object.entries(fraud.disputes.byReason || {}).map(([reason, ct]) => (
                    <div key={reason} className="rpt-kv"><span>{reason.replace(/_/g, ' ')}</span><strong>{ct}</strong></div>
                  ))}
                  {Object.keys(fraud.disputes.byReason || {}).length === 0 && <p className="rpt-empty-text">No dispute reasons recorded</p>}
                </div>
              </div>

              <div className="rpt-row-2">
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><Fingerprint size={18} /> Fraud Warning Types</h3>
                  {Object.entries(fraud.warnings.byType || {}).map(([type, ct]) => (
                    <div key={type} className="rpt-kv"><span>{type.replace(/_/g, ' ')}</span><strong>{ct}</strong></div>
                  ))}
                  {Object.keys(fraud.warnings.byType || {}).length === 0 && <p className="rpt-empty-text">No fraud warnings detected</p>}
                </div>
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><BarChart3 size={18} /> Risk Metrics</h3>
                  <div className="rpt-kv-list">
                    <div className="rpt-kv"><span>Total Charges Analyzed</span><strong>{fraud.riskMetrics.totalCharges}</strong></div>
                    <div className="rpt-kv"><span>Elevated/Highest Risk</span><strong className="text-red">{fraud.riskMetrics.riskyCharges}</strong></div>
                    <div className="rpt-kv"><span>Blocked by Radar</span><strong className="text-red">{fraud.riskMetrics.blockedCharges}</strong></div>
                    <div className="rpt-kv"><span>Dispute Rate</span><strong>{fraud.riskMetrics.disputeRate}%</strong></div>
                  </div>
                </div>
              </div>

              {fraud.disputes.recent.length > 0 && (
                <div className="rpt-card">
                  <h3 className="rpt-card-title"><FileBarChart size={18} /> Recent Disputes</h3>
                  <div className="rpt-table-wrap"><table className="rpt-table">
                    <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Reason</th><th>Date</th></tr></thead>
                    <tbody>{fraud.disputes.recent.map(d => (
                      <tr key={d.id}><td className="rpt-td-mono">{d.id.slice(-8)}</td><td className="rpt-td-money">{fmt(d.amount)}</td>
                      <td><span className={`rpt-status rpt-status--${d.status === 'won' ? 'succeeded' : d.status === 'lost' ? 'failed' : 'pending'}`}>{d.status}</span></td>
                      <td>{d.reason?.replace(/_/g, ' ') || '—'}</td><td>{fmtD(d.created)}</td></tr>
                    ))}</tbody>
                  </table></div>
                </div>
              )}
            </div>
          ) : <div className="rpt-empty">No trust &amp; safety activity yet</div>
        )}

        {/* ═══ PAYOUTS ═══ */}
        {tab === 'payouts' && (
          loading.pout ? <div className="rpt-loading"><Spin /> Loading payout report...</div> : errors.pout ? <PanelLoadError what="the payout report" error={errors.pout} onRetry={loadPayouts} /> : payoutData ? (
            <div className="rpt-section">
              <div className="rpt-metric-grid">
                <Metric icon={Wallet} label="Total Paid Out" value={fmt(payoutData.totalPaid)} sub={`${payoutData.total} payouts`} color="green" />
                <Metric icon={Clock} label="Pending" value={fmt(payoutData.totalPending)} color="yellow" />
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><Wallet size={18} /> Payout Status</h3>
                {Object.entries(payoutData.byStatus || {}).map(([st, ct]) => (
                  <div key={st} className="rpt-kv"><span><span className={`rpt-dot rpt-dot--${st}`} /> {st}</span><strong>{ct}</strong></div>
                ))}
              </div>

              <div className="rpt-card">
                <h3 className="rpt-card-title"><FileText size={18} /> Recent Payouts</h3>
                <div className="rpt-table-wrap"><table className="rpt-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Method</th><th>Arrival</th></tr></thead>
                  <tbody>{(payoutData.payouts || []).map(p => (
                    <tr key={p.id}><td className="rpt-td-mono">{p.id.slice(-8)}</td><td className="rpt-td-money">{fmt(p.amount)}</td>
                    <td><span className={`rpt-status rpt-status--${p.status === 'paid' ? 'succeeded' : 'pending'}`}>{p.status}</span></td>
                    <td>{p.method || '—'}</td><td>{fmtD(p.arrival_date)}</td></tr>
                  ))}{(payoutData.payouts || []).length === 0 && <tr><td colSpan="5" className="rpt-empty-text">No payouts</td></tr>}</tbody>
                </table></div>
              </div>
            </div>
          ) : <div className="rpt-empty">No payouts yet</div>
        )}
      </div>
    </div>
  );
}

export default StripeReportsPage;
